import { useState } from "react";
import api from "../api";

export default function StoryImages({ storyId, initialImages, onRefresh }) {
  const [images, setImages] = useState(initialImages || null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [error, setError] = useState(null);
  const [refinement, setRefinement] = useState({});
  const [refining, setRefining] = useState(null);

  const STEPS = [
    { delay: 0, text: "Claude מנתח את הסיפור..." },
    { delay: 8000, text: "יוצר תמונה ראשית..." },
    { delay: 25000, text: "יוצר תמונות תוכן..." },
    { delay: 50000, text: "מסיים..." },
  ];

  const generateImages = async () => {
    setLoading(true);
    setError(null);
    const timers = STEPS.map(({ delay, text }) => setTimeout(() => setLoadingStep(text), delay));
    try {
      const data = await api.generateStoryImages(storyId);
      setImages(data);
      onRefresh?.();
    } catch (e) {
      setError(e.message);
    } finally {
      timers.forEach(clearTimeout);
      setLoading(false);
      setLoadingStep("");
    }
  };

  const regenerate = async (imageKey) => {
    setRefining(imageKey);
    try {
      const data = await api.regenerateStoryImage(storyId, imageKey, refinement[imageKey] || "");
      setImages((prev) => {
        if (imageKey === "featured") {
          return { ...prev, featured: { ...prev.featured, b64: data.b64, size_kb: data.size_kb } };
        }
        const idx = parseInt(imageKey.split("_")[1]);
        const newInline = [...prev.inline];
        newInline[idx] = { ...newInline[idx], b64: data.b64, size_kb: data.size_kb };
        return { ...prev, inline: newInline };
      });
      setRefinement((prev) => ({ ...prev, [imageKey]: "" }));
    } catch (e) {
      setError(e.message);
    } finally {
      setRefining(null);
    }
  };

  const totalImages = images ? [images.featured, ...(images.inline || [])].filter(Boolean).length : 0;

  const ImageCard = ({ label, imageKey, imageData }) => (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-secondary)" }}>{label}</span>
        {imageData?.size_kb && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{imageData.size_kb}KB</span>}
      </div>
      {imageData?.b64 && (
        <img
          src={"data:image/jpeg;base64," + imageData.b64}
          alt={imageData.alt || ""}
          style={{ width: "100%", borderRadius: 6, maxHeight: 200, objectFit: "cover", marginBottom: 8 }}
        />
      )}
      {imageData?.alt && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>Alt: {imageData.alt}</div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="text"
          placeholder="שנה תיאור תמונה (Enter לאישור)"
          value={refinement[imageKey] || ""}
          onChange={(e) => setRefinement((prev) => ({ ...prev, [imageKey]: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && regenerate(imageKey)}
          style={{ flex: 1, fontSize: 13, padding: "6px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)" }}
          dir="rtl"
          disabled={refining === imageKey}
        />
        <button
          onClick={() => regenerate(imageKey)}
          disabled={refining === imageKey}
          style={{ padding: "6px 12px", background: "var(--surface-hover)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 12, cursor: refining === imageKey ? "wait" : "pointer" }}
        >
          {refining === imageKey ? "⏳" : "🔄"}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>תמונות לסיפור</div>
          {images && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
              {totalImages} תמונות מוכנות
            </div>
          )}
        </div>
        <button
          onClick={generateImages}
          disabled={loading}
          style={{ padding: "6px 14px", background: "var(--surface-hover)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, cursor: loading ? "wait" : "pointer" }}
        >
          {loading ? "⏳" : images ? "🔄 צור מחדש" : "✨ צור תמונות"}
        </button>
      </div>

      {loading && (
        <div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: "var(--radius)", padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 16, height: 16, border: "2px solid var(--accent)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <div>
            <div style={{ fontSize: 13, color: "var(--accent)", fontWeight: 500 }}>{loadingStep || "מתחיל..."}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>כ-60-90 שניות</div>
          </div>
          <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
        </div>
      )}

      {error && (
        <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "var(--radius)", padding: "10px 14px", color: "var(--red)", fontSize: 13, marginBottom: 12, display: "flex", justifyContent: "space-between" }}>
          <span>שגיאה: {error}</span>
          <span onClick={() => setError(null)} style={{ cursor: "pointer" }}>✕</span>
        </div>
      )}

      {images && (
        <div>
          {images.featured && (
            <ImageCard label="תמונה ראשית" imageKey="featured" imageData={images.featured} />
          )}
          {images.inline?.map((img, i) => (
            <ImageCard
              key={i}
              label={"תמונה " + (i + 1) + " - אחרי \"" + img.insert_after_heading + "\""}
              imageKey={"inline_" + i}
              imageData={img}
            />
          ))}
        </div>
      )}
    </div>
  );
}
